import { Component, ElementRef, NgZone, OnDestroy, OnInit } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  skip,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import { AuthService, UserAccess } from '../../services/auth.service';
import { BucketService } from '../../services/bucket.service';
import { ChatPreviewService } from '../../services/chat-preview.service';
import { CreditBalance, CreditService } from '../../services/credit.service';
import { LmsRegistryService } from '../../services/lms-registry.service';
import { OrganisationFeatures, OrganisationService } from '../../services/organisation.service';
import { ChatService } from '../../services/chat.service';
import { ConversationsService } from '../../services/conversations.service';
import { UserService } from '../../services/user.service';
import { FREE_PLAN_ID, isFreemailAddress } from '../../utils/trial-eligibility';
import { Tutor, TutorService, TutorTemplate } from '../../services/tutor.service';
import { HintAnimation } from './hint-motion';
import { HINT_FILMS, HintId } from './hint-films';
import { hasLinkedFolder, PREVIEW_TRIED_KEY, readKnowledgeMode } from './portal-state';
import { lessonForHint } from '../../academy/academy-catalog';
import { ORBI_IDLE } from '../orbi-lab/orbi-lab.component';
import { AgentSpotlightService } from '../../agent/agent-spotlight.service';
import { AgentChatService } from '../../agent/agent-chat.service';
import { AcademyService } from '../../academy/academy.service';

interface HintDef {
  /** Stabile Kennung — Grundlage des Wegklick-Schlüssels, nie umbenennen.
   *  Zugleich der Schlüssel des Films (`hint-films.ts`) und der verknüpften
   *  Academy-Lektion (`academy-catalog.ts`, `lessonForHint`). */
  id: HintId;
  titleKey: string;
  /** Linktext des Portal-Links; darf `{{name}}` (Agentname) enthalten. */
  linkKey?: string;
  /** Feste externe Ziele unter dem Film (öffnen in neuem Tab). */
  externalLinks?: { labelKey: string; url: string }[];
  /** Was „Gehe zu“ am Ziel hervorhebt: Katalog-Anker (`portal-ui-catalog.json`,
   *  `setting.<variable_name>`) und der Satz in der Blase daneben. Ohne Anker
   *  navigiert der Link nur. */
  spotlight?: { anchor: string; bodyKey: string };
  /** Statt eines Links eine Aktion an Ort und Stelle (Knopf „Öffnen“);
   *  `actionKey` = Text daneben. */
  action?: 'open-orbi';
  actionKey?: string;
  animation: () => HintAnimation;
}

/** Ein angezeigter Hinweis: Definition plus Ziel (und ggf. der Agent). */
interface ActiveHint {
  def: HintDef;
  /** Eindeutig je angezeigtem Hinweis (bei 2 und 3 mit Agent) — ein Wechsel
   *  des Agenten lässt den Film weiterlaufen. */
  key: string;
  /** Was das X sich merkt — meist `def.id` (ganzer Hinweis); Hinweis 4 bei
   *  leeren Credits ein eigener Schlüssel (siehe `findCreditsTrial`). */
  dismissKey: string;
  /** Ziel im Portal; leer = nur externe Links. */
  link: string;
  agentName?: string;
  /** Abweichender Linktext / Spotlight für diesen Fall (Hinweis F: erst das
   *  Interface anlegen, dann am Agenten wählen). */
  linkKey?: string;
  spotlight?: { anchor: string; bodyKey: string };
}

/** Hinweis 1 — Agent vorhanden, aber noch kein Wissensbasis-Ordner. */
const CREATE_KNOWLEDGE_BASE: HintDef = {
  id: 'create-knowledge-base',
  titleKey: 'portalHints.createKnowledgeBase.title',
  linkKey: 'portalHints.createKnowledgeBase.link',
  spotlight: { anchor: 'knowledge.create-bucket', bodyKey: 'portalHints.createKnowledgeBase.spotlight' },
  animation: HINT_FILMS['create-knowledge-base'],
};

/** Hinweis 2 — Ordner vorhanden, aber ein Agent hat keinen verknüpft. */
const LINK_KNOWLEDGE_BASE: HintDef = {
  id: 'link-knowledge-base',
  titleKey: 'portalHints.linkKnowledgeBase.title',
  linkKey: 'portalHints.linkKnowledgeBase.link',
  animation: HINT_FILMS['link-knowledge-base'],
};

/** Hinweis 3 — Agent hat Ordner, antwortet aber „Uneingeschränkt“ (`knowledge_mode = open`). */
const LIMIT_KNOWLEDGE: HintDef = {
  id: 'limit-knowledge',
  titleKey: 'portalHints.limitKnowledge.title',
  linkKey: 'portalHints.limitKnowledge.link',
  spotlight: { anchor: 'setting.knowledge_mode', bodyKey: 'portalHints.limitKnowledge.spotlight' },
  animation: HINT_FILMS['limit-knowledge'],
};

/** Hinweis 4 — neu im Free-Plan, Wissen ist eingerichtet und der Agent wird
 *  ausprobiert (Play-Button / Vorschau-Chat): Das Free-Kontingent reicht nur
 *  für wenige Nachrichten, und viele finden den Testzeitraum nicht (Marcel,
 *  15.10.2026). Feuert beim ersten Ausprobieren — oder sobald die Credits leer
 *  sind. */
const CREDITS_TRIAL: HintDef = {
  id: 'credits-trial',
  titleKey: 'portalHints.creditsTrial.title',
  linkKey: 'portalHints.creditsTrial.link',
  animation: HINT_FILMS['credits-trial'],
};

/** Hinweis 5 — noch keine LMS-Verbindung (Moodle, ILIAS oder LTI). Die Links
 *  führen zu den Plugins selbst: Moodle-Marketplace und ILIAS-Release auf
 *  GitHub (wie der Download im ILIAS-Verbinden-Dialog). */
const CONNECT_LMS: HintDef = {
  id: 'connect-lms',
  titleKey: 'portalHints.connectLms.title',
  externalLinks: [
    { labelKey: 'portalHints.connectLms.moodle', url: 'https://marketplace.moodle.com/plugins/3144' },
    {
      labelKey: 'portalHints.connectLms.ilias',
      url: 'https://github.com/alphabees-25/ilias-plugin-alphabees/releases/latest',
    },
  ],
  animation: HINT_FILMS['connect-lms'],
};

/** Hinweis 6 — ein LMS ist verbunden, aber noch kein Agent in einem Kurs
 *  platziert. */
const PLACE_AGENTS: HintDef = {
  id: 'place-agents',
  titleKey: 'portalHints.placeAgents.title',
  linkKey: 'portalHints.placeAgents.link',
  animation: HINT_FILMS['place-agents'],
};

/** Hinweis A — Orbi, der Portal-Assistent, ist an, wurde aber noch nie
 *  geöffnet. Der Knopf öffnet ihn direkt (wie ⌘K). */
const MEET_ORBI: HintDef = {
  id: 'meet-orbi',
  titleKey: 'portalHints.meetOrbi.title',
  action: 'open-orbi',
  actionKey: 'portalHints.meetOrbi.action',
  animation: HINT_FILMS['meet-orbi'],
};

/** Hinweis B — die Academy wurde noch nie geöffnet und keine Lektion begonnen. */
const VISIT_ACADEMY: HintDef = {
  id: 'visit-academy',
  titleKey: 'portalHints.visitAcademy.title',
  linkKey: 'portalHints.visitAcademy.link',
  spotlight: { anchor: 'academy.continue', bodyKey: 'portalHints.visitAcademy.spotlight' },
  animation: HINT_FILMS['visit-academy'],
};

/** Hinweis C — noch kein KI-Agent. */
const CREATE_AGENT: HintDef = {
  id: 'create-agent',
  titleKey: 'portalHints.createAgent.title',
  linkKey: 'portalHints.createAgent.link',
  spotlight: { anchor: 'agents.create', bodyKey: 'portalHints.createAgent.spotlight' },
  animation: HINT_FILMS['create-agent'],
};

/** Hinweis D — Wissen ist eingerichtet, der Vorschau-Chat wurde nie benutzt. */
const TRY_AGENT: HintDef = {
  id: 'try-agent',
  titleKey: 'portalHints.tryAgent.title',
  linkKey: 'portalHints.tryAgent.link',
  spotlight: { anchor: 'agents.card-play', bodyKey: 'portalHints.tryAgent.spotlight' },
  animation: HINT_FILMS['try-agent'],
};

/** Hinweis F — ein platzierter Agent zeigt noch das Standard-Design
 *  (`widget_setting_id` leer). Ohne eigenes Interface zuerst dorthin, sonst
 *  in die Allgemein-Seite des Agenten zur Auswahl. */
const STYLE_CHAT: HintDef = {
  id: 'style-chat',
  titleKey: 'portalHints.styleChat.title',
  linkKey: 'portalHints.styleChat.link',
  spotlight: { anchor: 'chat-styling.settings', bodyKey: 'portalHints.styleChat.spotlight' },
  animation: HINT_FILMS['style-chat'],
};

/** Hinweis G — es gibt echte Gespräche, die Seite Konversationen wurde aber
 *  noch nie geöffnet. */
const REVIEW_CONVERSATIONS: HintDef = {
  id: 'review-conversations',
  titleKey: 'portalHints.reviewConversations.title',
  linkKey: 'portalHints.reviewConversations.link',
  spotlight: { anchor: 'analytics.conversation-row', bodyKey: 'portalHints.reviewConversations.spotlight' },
  animation: HINT_FILMS['review-conversations'],
};

/** Hinweis H — die Organisation hat nur eine Person. */
const INVITE_TEAM: HintDef = {
  id: 'invite-team',
  titleKey: 'portalHints.inviteTeam.title',
  linkKey: 'portalHints.inviteTeam.link',
  spotlight: { anchor: 'org.add-user', bodyKey: 'portalHints.inviteTeam.spotlight' },
  animation: HINT_FILMS['invite-team'],
};

/** Solange der Vorschau-Chat offen ist, so oft den Credit-Stand nachsehen —
 *  das Kontingent läuft genau beim Testen über die Play-Buttons leer. */
const CREDIT_POLL_MS = 30_000;

/** Wie viele verknüpfte Agenten je Laden auf ihren Wissens-Modus geprüft
 *  werden (eine Anfrage je Agent) — der geöffnete Agent zählt immer mit. */
const MODE_CHECK_LIMIT = 3;

/** Agent-ID aus `/tutors/<id>/…` — der gerade geöffnete Agent hat Vorrang. */
const AGENT_URL = /^\/tutors\/([^/?#]+)/;

/** Orbi neben dem Film: Das erste Idle deckt das Herausgleiten und das
 *  Lab-Blinzeln ab (Tutor-Orbi blinzelt bei 2 s), dann kichern, freuen und
 *  10 s Ruhe, bevor es von vorn beginnt. Keine Sprünge nach rechts —
 *  Orbi steht schon am Rand der Box. */
const ORBI_SCRIPT = [ORBI_IDLE, 'giggle-bounce', ORBI_IDLE, 'hop-laugh', ORBI_IDLE, ORBI_IDLE, ORBI_IDLE, ORBI_IDLE];

const DISMISSED_KEY = 'al-portal-hints-dismissed-v1';
/** Wegklick-Schlüssel für die zweite (letzte) Gelegenheit von Hinweis 4. */
const CREDITS_EXHAUSTED_KEY = 'credits-trial:exhausted';
/** So lange steht „Erledigt ✓“, bevor der nächste Hinweis kommt. */
const DONE_FLASH_MS = 2600;
/** Merkt sich, dass der Vorschau-Chat schon benutzt wurde — Hinweis 4 bleibt
 *  dann auch nach dem Neuladen, bis der Testzeitraum läuft oder er weg ist. */
const TRIED_KEY = PREVIEW_TRIED_KEY;
/** Merkmale je Nutzer: Orbi schon geöffnet, Academy / Konversationen schon besucht. */
const ORBI_OPENED_KEY = 'al-portal-hints-orbi-v1';
const ACADEMY_SEEN_KEY = 'al-portal-hints-academy-v1';
const CONVERSATIONS_SEEN_KEY = 'al-portal-hints-conversations-v1';
/** Ab so vielen Gesprächen (30 Tage) lohnt der Blick in Konversationen. */
const CONVERSATIONS_MIN = 5;
/** Abstand der Box zur Seitenleiste und zum unteren Rand. */
const GAP = 20;

/**
 * Portal-Hinweise: kleine Box unten links im Inhaltsbereich mit Titel,
 * Erklärfilm (Loop) und Link zur Stelle, an der man es erledigt.
 *
 * Immer höchstens ein Hinweis. Er verschwindet von selbst, sobald seine
 * Bedingung nicht mehr gilt (z. B. erster Ordner angelegt), und lässt sich
 * wegklicken — das merkt sich der Browser je Nutzer und Hinweis.
 * Skripte und Bedingungen: features/portal-hints/claude-design-skripte.md.
 */
@Component({
  selector: 'app-portal-hints',
  templateUrl: './portal-hints.component.html',
  styleUrls: ['./portal-hints.component.scss'],
})
export class PortalHintsComponent implements OnInit, OnDestroy {
  hint: ActiveHint | null = null;
  animation: HintAnimation | null = null;
  left = GAP;
  onTargetPage = false;
  readonly orbiScript = ORBI_SCRIPT;

  private tutors: Tutor[] | null = null;
  private bucketCount: number | null = null;
  /** Vorlagen-IDs von Alpha Twin (`alpha_dozent`): der Twin bekommt sein
   *  Wissen über den verlinkten Tutor, ein eigener Ordner ist optional. */
  private twinTemplateIds = new Set<string>();
  /** Wissens-Modus je Agent (`knowledge_mode`); fehlt = noch nicht geladen. */
  private knowledgeModes = new Map<string, string>();
  private modeRequests = new Set<string>();
  /** LMS-Verbindungen der Organisation; `null` = noch nicht geladen. Ein
   *  Fehler (z. B. fehlende Rolle) ergibt -1 — dann kein Hinweis 5. */
  private lmsRegistrationCount: number | null = null;
  private registrationsRequested = false;
  /** Kurs-Sync über Platzierungen eingeschaltet? `null` = noch nicht geprüft. */
  private courseSyncOn: boolean | null = null;
  private courseSyncRequested = false;
  /** Credits/Kontingent der Organisation aufgebraucht? `null` = unbekannt. */
  private creditsExhausted: boolean | null = null;
  /** Agent schon über den Vorschau-Chat ausprobiert (je Nutzer, im Browser). */
  private previewTried = false;
  /** Hinweis A: Portal-Assistent an? `null` = Status lädt. */
  private orbiEnabled: boolean | null = null;
  /** Orbi schon benutzt (geöffnet oder ein Gespräch); `null` = Gespräche laden. */
  private orbiUsed: boolean | null = null;
  /** Hinweis B: Academy schon besucht oder eine Lektion begonnen; `null` = lädt. */
  private academyStarted: boolean | null = null;
  /** Hinweis F: Zahl eigener Interfaces; `null` = noch nicht geladen. */
  private chatStyleCount: number | null = null;
  private chatStylesRequested = false;
  /** Hinweis G: Gespräche der letzten 30 Tage; `null` = noch nicht geladen. */
  private conversationCount: number | null = null;
  private conversationsRequested = false;
  /** Hinweis H: Personen der Organisation; `null` = noch nicht geladen. */
  private userCount: number | null = null;
  private usersRequested = false;
  private readonly orbiOpen$: Observable<boolean>;
  private dismissed = new Set<string>();
  /** Der zuletzt weggeklickte Hinweis. Solange seine Aufgabe offen ist, bleibt
   *  die Box bis zum nächsten Laden zu — sonst rückte sofort der nächste Agent
   *  oder Hinweis nach, und das X schien nicht zu wirken. Erledigt man die
   *  Aufgabe trotzdem (z. B. Ordner verknüpft), kommt der nächste Schritt
   *  gleich (Marcel, 19.10.2026: Hinweis 3 blieb nach dem Verknüpfen aus). */
  private closedKey: string | null = null;
  /** „Erledigt ✓“ läuft gerade — der nächste Hinweis wartet so lange. */
  doneFlash = false;
  private doneTimer?: ReturnType<typeof setTimeout>;
  private resizeObserver?: ResizeObserver;
  private dataSub?: Subscription;
  private readonly destroy$ = new Subject<void>();
  private readonly onWindowResize = () => this.zone.run(() => this.measureLeft());

  constructor(
    private authService: AuthService,
    private bucketService: BucketService,
    private tutorService: TutorService,
    private lmsRegistry: LmsRegistryService,
    private creditService: CreditService,
    private organisationService: OrganisationService,
    private chatPreviewService: ChatPreviewService,
    private router: Router,
    private zone: NgZone,
    private host: ElementRef<HTMLElement>,
    private spotlight: AgentSpotlightService,
    private translate: TranslateService,
    private agentChat: AgentChatService,
    private academy: AcademyService,
    private chatService: ChatService,
    private conversations: ConversationsService,
    private userService: UserService,
  ) {
    this.orbiOpen$ = toObservable(this.agentChat.open);
  }

  /** Hinweis A: „Öffnen“ öffnet Orbi an Ort und Stelle (wie ⌘K). */
  runAction(): void {
    if (this.hint?.def.action === 'open-orbi') this.agentChat.openPanel();
  }

  /** Die Academy-Lektion zum gezeigten Hinweis („Mehr erfahren“). */
  get academyLesson(): string | null {
    return this.hint ? (lessonForHint(this.hint.def.id)?.id ?? null) : null;
  }

  /** „Gehe zu“: navigiert über `routerLink`; ist die Seite da, zeigt der
   *  Spotlight das Ziel (sucht es bis zu 3 s, z. B. bis „Quellen & Suche“
   *  seine Einstellungen geladen hat). Der Hinweis wird beim Klick erfasst —
   *  nach der Navigation steht oft schon ein anderer. */
  go(): void {
    const target = this.hint?.spotlight ?? this.hint?.def.spotlight;
    if (!target || !this.hint) return;
    const step = {
      anchor: target.anchor,
      title: this.translate.instant(this.hint.def.titleKey),
      body: this.translate.instant(target.bodyKey),
    };
    this.router.events
      .pipe(
        filter(
          (e) => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError,
        ),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe((e) => {
        if (e instanceof NavigationEnd) this.spotlight.highlight(step);
      });
  }

  ngOnInit(): void {
    this.authService.accessToken$
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((token) => this.reload(!!token));

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.markVisited();
        this.evaluate();
      });
    this.updateOnTargetPage();

    this.observeSidebar();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.dataSub?.unsubscribe();
    clearTimeout(this.doneTimer);
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.onWindowResize);
  }

  /** X: Dieser Hinweis ist übersprungen — er kommt für keinen Agenten wieder
   *  (gemerkt im Browser), und bis zum nächsten Laden bleibt die Box zu; dann
   *  folgt der nächste offene Schritt. Einzige Ausnahme: Hinweis 4 kommt genau
   *  einmal wieder, wenn später die Credits leer sind. */
  dismiss(): void {
    if (!this.hint) return;
    this.dismissed.add(this.hint.dismissKey);
    // Bei schon leeren Credits gibt es keinen späteren Anlass mehr.
    if (this.hint.def === CREDITS_TRIAL && this.creditsExhausted) this.dismissed.add(CREDITS_EXHAUSTED_KEY);
    this.closedKey = this.hint.key;
    try {
      localStorage.setItem(this.storageKey(), JSON.stringify([...this.dismissed]));
    } catch {
      // Ohne Speicher gilt das Wegklicken nur bis zum Neuladen.
    }
    this.evaluate();
  }

  /** Lädt Agenten und Ordner neu (Anmeldung, Mandantenwechsel). */
  private reload(signedIn: boolean): void {
    this.dataSub?.unsubscribe();
    this.tutors = null;
    this.bucketCount = null;
    this.knowledgeModes.clear();
    this.modeRequests.clear();
    this.courseSyncOn = null;
    this.courseSyncRequested = false;
    this.lmsRegistrationCount = null;
    this.registrationsRequested = false;
    this.creditsExhausted = null;
    this.orbiEnabled = null;
    this.orbiUsed = null;
    this.academyStarted = null;
    this.chatStyleCount = null;
    this.chatStylesRequested = false;
    this.conversationCount = null;
    this.conversationsRequested = false;
    this.userCount = null;
    this.usersRequested = false;
    this.closedKey = null;
    clearTimeout(this.doneTimer);
    this.doneFlash = false;
    this.dismissed = this.readDismissed();
    this.previewTried = this.readFlag(TRIED_KEY);
    this.evaluate();
    if (!signedIn) return;
    this.markVisited();

    // `skip(1)`: beide Listen sind BehaviorSubjects mit [] als Startwert —
    // erst die Antwort auf die eigene Anfrage zählt, sonst blitzte der
    // Hinweis bei jedem Laden kurz auf. Spätere Werte (Ordner angelegt,
    // Agent gelöscht) kommen über dieselben Subjects.
    this.dataSub = combineLatest([
      this.tutorService.tutors$.pipe(skip(1)),
      this.bucketService._buckets.pipe(skip(1)),
      this.tutorService.listTutorTemplates().pipe(catchError(() => of([] as TutorTemplate[]))),
    ]).subscribe(([tutors, buckets, templates]) => {
      this.tutors = tutors ?? [];
      this.bucketCount = buckets?.length ?? 0;
      this.twinTemplateIds = new Set(
        (templates ?? []).filter((t) => t.name === 'alpha_dozent').map((t) => t.id),
      );
      this.evaluate();
    });
    this.dataSub.add(this.tutorService.listTutors().subscribe({ error: () => undefined }));
    // Stellt jemand auf Quellen & Suche den Wissens-Modus um, landet der neue
    // Wert in diesem Cache — der Hinweis verschwindet sofort.
    this.dataSub.add(
      this.tutorService.tutorSettings$.pipe(skip(1)).subscribe((items) => {
        const botId = items?.[0]?.bot_id;
        const mode = readKnowledgeMode(items);
        if (!botId || mode === null) return;
        this.knowledgeModes.set(botId, mode);
        this.evaluate();
      }),
    );
    this.bucketService.listBuckets();
    this.loadOrbi();
    this.loadAcademy();

    // Hinweis 4: Credit-Stand beim Laden, alle 30 s solange der Vorschau-Chat
    // offen ist, und einmal nach dem Schließen.
    this.dataSub.add(this.organisationService.organisation$.subscribe(() => this.evaluate()));
    this.dataSub.add(
      this.chatPreviewService.isChatActive$
        .pipe(
          distinctUntilChanged(),
          tap((active) => {
            if (active && !this.previewTried) {
              this.previewTried = true;
              this.writeFlag(TRIED_KEY);
              this.evaluate();
            }
          }),
          switchMap((active) => (active ? timer(CREDIT_POLL_MS, CREDIT_POLL_MS) : timer(0))),
          switchMap(() => this.creditService.getBalance().pipe(catchError(() => of(null)))),
        )
        .subscribe((balance) => {
          if (balance) this.creditsExhausted = isExhausted(balance);
          this.evaluate();
        }),
    );
  }

  /**
   * Geht die Schritte in fester Reihenfolge durch (`steps`) und zeigt den
   * ersten offenen, nicht weggeklickten. Ist der bisher gezeigte Hinweis
   * erledigt, kommt erst kurz „Erledigt ✓“, dann der nächste.
   */
  private evaluate(): void {
    if (this.doneFlash) return; // läuft gerade, der Timer wertet danach neu aus

    const { pick, openKeys, passed } = this.walk();
    const prev = this.hint;

    if (pick?.key === prev?.key) {
      this.hint = pick; // gleicher Hinweis — Agentname/Link können sich ändern
      this.updateOnTargetPage();
      return;
    }

    const prevDone =
      !!prev &&
      passed.has(prev.def.id) &&
      !openKeys.has(prev.key) &&
      !this.dismissed.has(prev.dismissKey);
    if (prevDone) {
      this.doneFlash = true;
      this.doneTimer = setTimeout(() => {
        this.doneFlash = false;
        this.show(this.walk().pick);
      }, DONE_FLASH_MS);
      return;
    }

    this.show(pick);
  }

  private show(next: ActiveHint | null): void {
    // Gleicher Film (anderer Agent): Film weiterlaufen lassen.
    if (next?.def !== this.hint?.def) this.animation = next ? next.def.animation() : null;
    this.hint = next;
    if (next) this.measureLeft();
    this.updateOnTargetPage();
  }

  /**
   * Die Reihenfolge der Portal-Hinweise — wer sie ändern will, verschiebt
   * eine Zeile. Jeder Schritt prüft den echten Zustand, nicht ob der vorige
   * gezeigt wurde; Erledigtes fällt so von selbst heraus (wer zuerst Moodle
   * verbindet und Kurs-Sync einschaltet, sieht Hinweis 1 und 5 nie).
   * `urgent`: kommt auch, nachdem in dieser Sitzung ein Hinweis weggeklickt
   * wurde — er hat einen neuen Anlass.
   */
  private readonly steps: { def: HintDef; find: () => ActiveHint[] | 'pending'; urgent?: boolean }[] = [
    { def: CREDITS_TRIAL, find: () => this.findCreditsTrial(), urgent: true },
    { def: MEET_ORBI, find: () => this.findMeetOrbi() },
    { def: VISIT_ACADEMY, find: () => this.findVisitAcademy() },
    { def: CREATE_AGENT, find: () => this.findCreateAgent() },
    { def: CREATE_KNOWLEDGE_BASE, find: () => this.findCreateKnowledgeBase() },
    { def: LINK_KNOWLEDGE_BASE, find: () => this.findLinkKnowledgeBase() },
    { def: LIMIT_KNOWLEDGE, find: () => this.findLimitKnowledge() },
    { def: TRY_AGENT, find: () => this.findTryAgent() },
    { def: CONNECT_LMS, find: () => this.findConnectLms() },
    { def: PLACE_AGENTS, find: () => this.findPlaceAgents() },
    { def: STYLE_CHAT, find: () => this.findStyleChat() },
    { def: REVIEW_CONVERSATIONS, find: () => this.findReviewConversations() },
    { def: INVITE_TEAM, find: () => this.findInviteTeam() },
  ];

  /**
   * Erster offener Schritt. `pending` (Daten laden noch) hält den Durchlauf an
   * — sonst blitzte kurz ein späterer Hinweis auf, bis der frühere geladen ist.
   * `openKeys`/`passed` sagen, ob der bisher gezeigte Hinweis erledigt ist.
   */
  private walk(): { pick: ActiveHint | null; openKeys: Set<string>; passed: Set<string> } {
    const openKeys = new Set<string>();
    const passed = new Set<string>();
    // Gesperrt ab dem Schritt, dessen weggeklickte Aufgabe noch offen ist.
    let blocked = false;
    for (const step of this.steps) {
      const found = step.find();
      if (found === 'pending') break;
      passed.add(step.def.id);
      found.forEach((h) => openKeys.add(h.key));
      if (this.closedKey && found.some((h) => h.key === this.closedKey)) blocked = true;
      const pick = found.find((h) => !this.dismissed.has(h.dismissKey));
      if (pick && (step.urgent || !blocked)) return { pick, openKeys, passed };
    }
    return { pick: null, openKeys, passed };
  }

  /** Geöffneter Agent zuerst, dann die neuesten. */
  private byPriority(list: Tutor[]): Tutor[] {
    const openId = AGENT_URL.exec(this.router.url)?.[1];
    return [...list].sort(
      (a, b) =>
        Number(b.id === openId) - Number(a.id === openId) ||
        +new Date(b.created_at) - +new Date(a.created_at),
    );
  }

  /** Hinweis 4: beim Ausprobieren (Wissen eingerichtet) oder bei leeren
   *  Credits. Wer ihn beim Ausprobieren weggeklickt hat, sieht ihn genau
   *  einmal wieder, wenn die Credits leer sind — eigener Wegklick-Schlüssel. */
  private findCreditsTrial(): ActiveHint[] {
    if (!this.canStartTrial()) return [];
    if (!this.creditsExhausted && !(this.previewTried && this.knowledgeReady())) return [];
    const dismissKey =
      this.creditsExhausted && this.dismissed.has(CREDITS_TRIAL.id) ? CREDITS_EXHAUSTED_KEY : CREDITS_TRIAL.id;
    return [{ def: CREDITS_TRIAL, key: CREDITS_TRIAL.id, dismissKey, link: '/products' }];
  }

  /** Hinweis 1: Agent da, kein Ordner — außer Kurs-Sync läuft (dann ist das
   *  Kurswissen die Wissensbasis; Marcel, 15.10.2026). */
  private findCreateKnowledgeBase(): ActiveHint[] | 'pending' {
    if (this.tutors === null || this.bucketCount === null) return 'pending';
    if (!this.tutors.length || this.bucketCount > 0) return [];
    if (this.courseSyncOn === null) {
      this.loadCourseSync();
      return 'pending';
    }
    if (this.courseSyncOn) return [];
    const def = CREATE_KNOWLEDGE_BASE;
    return [{ def, key: def.id, dismissKey: def.id, link: '/knowledge-base' }];
  }

  /** Hinweis 2: Ordner da, aber ein Agent ohne verknüpften Ordner. */
  private findLinkKnowledgeBase(): ActiveHint[] | 'pending' {
    if (this.tutors === null || this.bucketCount === null) return 'pending';
    if (!this.bucketCount) return [];
    const def = LINK_KNOWLEDGE_BASE;
    return this.byPriority(this.tutors.filter((t) => this.lacksKnowledge(t)))
      .map((t) => ({
        def,
        key: `${def.id}:${t.id}`,
        dismissKey: def.id,
        link: `/tutors/${t.id}/knowledge/base`,
        agentName: t.visual_name || t.name,
      }));
  }

  /** Hinweis 3: Agent mit Ordner steht auf „Uneingeschränkt“. Geprüft werden
   *  höchstens `MODE_CHECK_LIMIT` Agenten (eine Settings-Anfrage je Agent). */
  private findLimitKnowledge(): ActiveHint[] | 'pending' {
    if (this.tutors === null) return 'pending';
    const def = LIMIT_KNOWLEDGE;
    if (this.dismissed.has(def.id)) return [];
    const linked = this.byPriority(
      this.tutors.filter((t) => !this.isTwin(t) && this.hasLinkedFolder(t)),
    ).slice(0, MODE_CHECK_LIMIT);
    this.loadKnowledgeModes(linked);
    if (linked.some((t) => !this.knowledgeModes.has(t.id))) return 'pending';
    return linked
      .filter((t) => this.knowledgeModes.get(t.id) === 'open')
      .map((t) => ({
        def,
        key: `${def.id}:${t.id}`,
        dismissKey: def.id,
        link: `/tutors/${t.id}/knowledge/usage`,
        agentName: t.visual_name || t.name,
      }));
  }

  /** Hinweis 5: Wissen ist da, aber noch kein LMS angebunden. */
  private findConnectLms(): ActiveHint[] | 'pending' {
    const def = CONNECT_LMS;
    if (this.dismissed.has(def.id) || !this.knowledgeReady()) return [];
    this.loadRegistrations();
    if (this.lmsRegistrationCount === null) return 'pending';
    return this.lmsRegistrationCount === 0 ? [{ def, key: def.id, dismissKey: def.id, link: '' }] : [];
  }

  /** Hinweis 6: LMS verbunden (Moodle, ILIAS, LTI), aber kein Agent ist in
   *  einem Kurs platziert (`live_course_count` = Kurse mit Platzierung). */
  private findPlaceAgents(): ActiveHint[] | 'pending' {
    const def = PLACE_AGENTS;
    if (this.tutors === null) return 'pending';
    if (this.dismissed.has(def.id) || !this.tutors.length) return [];
    // Kein Agent platziert — oder (Kunden mit Kurs-Sync, ohne eigene Ordner)
    // ein Agent ohne jedes Wissen: Hinweis 2 greift dort nicht, weil es
    // keinen Ordner zum Verknüpfen gibt; sein Weg zum Wissen ist die Platzierung.
    const nonePlaced = !this.tutors.some((t) => (t.live_course_count ?? 0) > 0);
    if (!nonePlaced) {
      if (this.bucketCount === null) return 'pending';
      if (this.bucketCount > 0 || !this.tutors.some((t) => this.lacksKnowledge(t))) return [];
    }
    this.loadRegistrations();
    if (this.lmsRegistrationCount === null) return 'pending';
    return this.lmsRegistrationCount > 0
      ? [{ def, key: def.id, dismissKey: def.id, link: '/tutors/placements' }]
      : [];
  }

  /** Hinweis A: Orbi ist an und wurde noch nie geöffnet. */
  private findMeetOrbi(): ActiveHint[] | 'pending' {
    const def = MEET_ORBI;
    if (this.dismissed.has(def.id)) return [];
    if (this.orbiEnabled === null) return 'pending';
    if (!this.orbiEnabled) return [];
    if (this.orbiUsed === null) return 'pending';
    return this.orbiUsed ? [] : [{ def, key: def.id, dismissKey: def.id, link: '' }];
  }

  /** Hinweis B: Academy nie besucht und keine Lektion begonnen. */
  private findVisitAcademy(): ActiveHint[] | 'pending' {
    const def = VISIT_ACADEMY;
    if (this.dismissed.has(def.id)) return [];
    if (this.academyStarted === null) return 'pending';
    return this.academyStarted ? [] : [{ def, key: def.id, dismissKey: def.id, link: '/academy' }];
  }

  /** Hinweis C: noch kein KI-Agent. */
  private findCreateAgent(): ActiveHint[] | 'pending' {
    const def = CREATE_AGENT;
    if (this.tutors === null) return 'pending';
    return this.tutors.length ? [] : [{ def, key: def.id, dismissKey: def.id, link: '/tutors' }];
  }

  /** Hinweis D: Wissen eingerichtet, aber der Vorschau-Chat nie benutzt. */
  private findTryAgent(): ActiveHint[] | 'pending' {
    const def = TRY_AGENT;
    if (this.tutors === null) return 'pending';
    if (this.dismissed.has(def.id) || this.previewTried || !this.knowledgeReady()) return [];
    return [{ def, key: def.id, dismissKey: def.id, link: '/tutors' }];
  }

  /** Hinweis F: ein platzierter Agent trägt noch das Standard-Design. Ohne
   *  eigenes Interface zuerst dorthin; gibt es eins, am Agenten auswählen. */
  private findStyleChat(): ActiveHint[] | 'pending' {
    const def = STYLE_CHAT;
    if (this.tutors === null) return 'pending';
    if (this.dismissed.has(def.id)) return [];
    const unstyled = this.byPriority(
      this.tutors.filter((t) => !this.isTwin(t) && (t.live_course_count ?? 0) > 0 && !t.widget_setting_id),
    );
    if (!unstyled.length) return [];
    this.loadChatStyles();
    if (this.chatStyleCount === null) return 'pending';
    if (this.chatStyleCount === 0) return [{ def, key: def.id, dismissKey: def.id, link: '/chat-styling' }];
    return unstyled.map((t) => ({
      def,
      key: `${def.id}:${t.id}`,
      dismissKey: def.id,
      link: `/tutors/${t.id}/general`,
      agentName: t.visual_name || t.name,
      linkKey: 'portalHints.styleChat.linkAgent',
      spotlight: { anchor: 'agent.widget-style', bodyKey: 'portalHints.styleChat.spotlightAgent' },
    }));
  }

  /** Hinweis G: mindestens `CONVERSATIONS_MIN` Gespräche in 30 Tagen, die
   *  Seite Konversationen aber nie geöffnet (nur mit Analytics-Zugriff). */
  private findReviewConversations(): ActiveHint[] | 'pending' {
    const def = REVIEW_CONVERSATIONS;
    if (this.dismissed.has(def.id) || this.readFlag(CONVERSATIONS_SEEN_KEY)) return [];
    const access =
      this.organisationService.hasFeature(OrganisationFeatures.ANALYTICS) &&
      this.authService.hasFeatureAccess('ANALYTICS');
    if (!access) return [];
    this.loadConversations();
    if (this.conversationCount === null) return 'pending';
    return this.conversationCount >= CONVERSATIONS_MIN
      ? [{ def, key: def.id, dismissKey: def.id, link: '/analytics/conversations' }]
      : [];
  }

  /** Hinweis H: Wissen eingerichtet, aber nur eine Person in der
   *  Organisation (nur für alle, die Personen verwalten dürfen). */
  private findInviteTeam(): ActiveHint[] | 'pending' {
    const def = INVITE_TEAM;
    if (this.dismissed.has(def.id) || !this.knowledgeReady()) return [];
    if (!this.authService.hasAccess(UserAccess.UserMenuOrganisation)) return [];
    this.loadUsers();
    if (this.userCount === null) return 'pending';
    return this.userCount === 1 ? [{ def, key: def.id, dismissKey: def.id, link: '/organisation' }] : [];
  }

  /** Hinweis A: Status des Assistenten, dann seine Gespräche — und ob er gerade
   *  geöffnet wird (das zählt als erledigt). */
  private loadOrbi(): void {
    this.orbiUsed = this.readFlag(ORBI_OPENED_KEY) ? true : null;
    this.agentChat.loadStatus().then(() => {
      this.orbiEnabled = this.agentChat.enabled();
      if (!this.orbiEnabled || this.orbiUsed) return this.evaluate();
      this.agentChat.loadThreads().then(() => {
        if (this.orbiUsed !== true) this.orbiUsed = this.agentChat.threads().length > 0;
        this.evaluate();
      });
    });
    this.dataSub?.add(
      this.orbiOpen$.pipe(filter((open) => open)).subscribe(() => {
        this.writeFlag(ORBI_OPENED_KEY);
        this.orbiUsed = true;
        this.evaluate();
      }),
    );
  }

  /** Hinweis B: Academy-Fortschritt (Server, sonst lokal) plus Besuchs-Merkmal. */
  private loadAcademy(): void {
    this.academyStarted = this.readFlag(ACADEMY_SEEN_KEY) ? true : null;
    this.academy.loadProgress();
    this.dataSub?.add(
      combineLatest([this.academy.progressLoaded$, this.academy.progress$]).subscribe(([loaded, progress]) => {
        if (!loaded || this.academyStarted) return;
        this.academyStarted = [...progress.keys()].some((k) => k.startsWith('lesson:'));
        this.evaluate();
      }),
    );
  }

  /** Hinweis F: eigene Interfaces einmal je Laden zählen. */
  private loadChatStyles(): void {
    if (this.chatStylesRequested) return;
    this.chatStylesRequested = true;
    const sub = this.chatService.listChatSettings().subscribe({
      next: (list: unknown[]) => {
        this.chatStyleCount = list?.length ?? 0;
        this.evaluate();
      },
      error: () => {
        this.chatStyleCount = -1; // unbekannt — dann der Weg über den Agenten
        this.evaluate();
      },
    });
    this.dataSub?.add(sub);
  }

  /** Hinweis G: Gespräche der letzten 30 Tage (nur die Gesamtzahl). */
  private loadConversations(): void {
    if (this.conversationsRequested) return;
    this.conversationsRequested = true;
    const sub = this.conversations
      .list({ days: 30, limit: 1 })
      .pipe(catchError(() => of(null)))
      .subscribe((res) => {
        this.conversationCount = res?.total ?? 0;
        this.evaluate();
      });
    this.dataSub?.add(sub);
  }

  /** Hinweis H: Personen der Organisation zählen. */
  private loadUsers(): void {
    if (this.usersRequested) return;
    this.usersRequested = true;
    const sub = this.userService
      .listUsers()
      .pipe(catchError(() => of(null)))
      .subscribe((users) => {
        // Fehler = unbekannt → kein Hinweis.
        this.userCount = users ? users.length : 0;
        this.evaluate();
      });
    this.dataSub?.add(sub);
  }

  /** Academy / Konversationen besucht: merken — Hinweis B / G sind erledigt. */
  private markVisited(): void {
    if (!this.authService.accessTokenPayload$.value) return;
    const path = this.router.url.split(/[?#]/)[0];
    if (path.startsWith('/academy') && !this.readFlag(ACADEMY_SEEN_KEY)) {
      this.writeFlag(ACADEMY_SEEN_KEY);
      this.academyStarted = true;
    }
    if (path === '/analytics/conversations') this.writeFlag(CONVERSATIONS_SEEN_KEY);
  }

  /** LMS-Verbindungen einmal je Laden zählen (Moodle, ILIAS, LTI). */
  private loadRegistrations(): void {
    if (this.registrationsRequested) return;
    this.registrationsRequested = true;
    const sub = this.lmsRegistry.listRegistrations().subscribe({
      next: (registrations) => {
        this.lmsRegistrationCount = registrations?.length ?? 0;
        this.evaluate();
      },
      // Unbekannt (z. B. fehlende Rolle) — dann kein Hinweis 5.
      error: () => {
        this.lmsRegistrationCount = -1;
        this.evaluate();
      },
    });
    this.dataSub?.add(sub);
  }

  /** Prüft einmal je Laden, ob irgendwo Kurs-Sync läuft: automatisch an einer
   *  LMS-Verbindung oder an einer einzelnen Platzierung (Moodle, ILIAS, LTI).
   *  Nur nötig, solange Hinweis 1 sonst erschiene — daher erst hier. Ein
   *  Fehler zählt als „kein Sync“; dann bleibt es beim normalen Hinweis. */
  private loadCourseSync(): void {
    if (this.courseSyncRequested) return;
    this.courseSyncRequested = true;
    const sub = this.lmsRegistry
      .listRegistrations()
      .pipe(
        switchMap((registrations): Observable<boolean> => {
          if (!registrations?.length) return of(false);
          if (registrations.some((r) => r.auto_course_sync_enabled)) return of(true);
          return forkJoin(
            registrations.map((r) =>
              this.lmsRegistry.listPlacements(r.id).pipe(catchError(() => of([]))),
            ),
          ).pipe(map((lists) => lists.some((list) => list.some((p) => p.course_sync_enabled))));
        }),
        catchError(() => of(false)),
      )
      .subscribe((on) => {
        this.courseSyncOn = on;
        this.evaluate();
      });
    this.dataSub?.add(sub);
  }

  /** Holt den Wissens-Modus der Agenten, die noch keinen haben — je Agent
   *  einmal pro Laden; das Ergebnis stößt eine neue Auswertung an. */
  private loadKnowledgeModes(agents: Tutor[]): void {
    for (const t of agents) {
      if (this.knowledgeModes.has(t.id) || this.modeRequests.has(t.id)) continue;
      this.modeRequests.add(t.id);
      const sub = this.tutorService.fetchTutorSettings(t.id).subscribe({
        next: (items) => {
          // Ohne Einstellung `knowledge_mode` zählt der Agent als erledigt ('').
          this.knowledgeModes.set(t.id, readKnowledgeMode(items) ?? '');
          this.evaluate();
        },
        // Ohne Antwort zählt der Agent als „nicht uneingeschränkt“.
        error: () => {
          this.knowledgeModes.set(t.id, '');
          this.evaluate();
        },
      });
      this.dataSub?.add(sub);
    }
  }

  /** Kein Ordner verknüpft und kein Kurswissen über Platzierungen (Kurs-Sync
   *  bringt eigenes Wissen mit). Alpha Twins zählen nicht — siehe oben. */
  private lacksKnowledge(t: Tutor): boolean {
    if (this.isTwin(t)) return false;
    if ((t.live_course_count ?? 0) > 0) return false;
    return !this.hasLinkedFolder(t);
  }

  /** Gleiche Regel wie der Knopf „2 Wochen Testzeitraum starten" auf der
   *  Produktkarte (Free-Plan, Trial ungenutzt, keine Freemail-Adresse) —
   *  sonst zeigte der Hinweis auf einen Knopf, den es nicht gibt. */
  private canStartTrial(): boolean {
    const org = this.organisationService.organisation$.value;
    if (!org) return false;
    return org.current_plan === FREE_PLAN_ID && org.trial_used === false && !isFreemailAddress(org.email);
  }

  /** Wissen ist eingerichtet: ein Agent mit verknüpftem Ordner (Kurs-Sync
   *  verknüpft seinen Ordner selbst) oder mit Kurswissen über Platzierungen. */
  private knowledgeReady(): boolean {
    return (this.tutors ?? []).some((t) => this.hasLinkedFolder(t) || (t.live_course_count ?? 0) > 0);
  }

  private hasLinkedFolder(t: Tutor): boolean {
    return hasLinkedFolder(t);
  }

  private isTwin(t: Tutor): boolean {
    return !!t.template_id && this.twinTemplateIds.has(t.template_id);
  }

  private updateOnTargetPage(): void {
    this.onTargetPage = !!this.hint?.link && this.router.url.split(/[?#]/)[0] === this.hint.link;
  }

  /** Die Box steht rechts neben der Seitenleiste — die ändert ihre Breite
   *  beim Ein-/Ausklappen, daher beobachten statt fest rechnen. */
  private observeSidebar(): void {
    window.addEventListener('resize', this.onWindowResize);
    const sidebar = this.host.nativeElement.closest('nb-layout')?.querySelector('nb-sidebar');
    if (sidebar && typeof ResizeObserver === 'function') {
      this.resizeObserver = new ResizeObserver(() => this.zone.run(() => this.measureLeft()));
      this.resizeObserver.observe(sidebar);
    }
    this.measureLeft();
  }

  private measureLeft(): void {
    const sidebar = this.host.nativeElement
      .closest('nb-layout')
      ?.querySelector<HTMLElement>('nb-sidebar .main-container');
    const right = sidebar?.getBoundingClientRect().right ?? 0;
    // Mobil liegt die Seitenleiste über dem Inhalt — dann am Rand bleiben.
    this.left = Math.max(0, Math.round(right)) + GAP;
    this.updateOnTargetPage();
  }

  private readFlag(key: string): boolean {
    try {
      return localStorage.getItem(`${key}:${this.userScope()}`) === '1';
    } catch {
      return false;
    }
  }

  private writeFlag(key: string): void {
    try {
      localStorage.setItem(`${key}:${this.userScope()}`, '1');
    } catch {
      // Ohne Speicher gilt es nur bis zum Neuladen.
    }
  }

  private userScope(): string {
    const p = this.authService.accessTokenPayload$.value;
    return `${p?.client_id ?? ''}:${p?.user_id ?? ''}`;
  }

  private storageKey(): string {
    const p = this.authService.accessTokenPayload$.value;
    return `${DISMISSED_KEY}:${p?.client_id ?? ''}:${p?.user_id ?? ''}`;
  }

  private readDismissed(): Set<string> {
    try {
      const raw = localStorage.getItem(this.storageKey());
      const list = raw ? JSON.parse(raw) : [];
      // Einträge je Agent (`link-knowledge-base:<agent>`, `limit-knowledge:<agent>`)
      // stammen aus der Fassung vor dem Schließen-Fix, als das X nur einen
      // Agenten ausblendete und man mehrmals klicken musste — keine bewusste
      // Entscheidung. Sie gelten nicht mehr (Marcel, 19.10.2026: ein neuer
      // Agent ohne Ordner bekam deshalb keinen Hinweis).
      const legacyPerAgent = /^(link-knowledge-base|limit-knowledge):/;
      return new Set(
        Array.isArray(list) ? list.filter((x) => typeof x === 'string' && !legacyPerAgent.test(x)) : [],
      );
    } catch {
      return new Set();
    }
  }
}

/** Kontingent leer: auf Credit-Basis 100 % verbraucht, sonst keine Tokens
 *  mehr (Plan + gekaufte Credits); `-1` = unbegrenzt. */
function isExhausted(b: CreditBalance): boolean {
  if (b.quota_basis === 'credits' && b.credit_percent_used != null) return b.credit_percent_used >= 100;
  if (b.plan_token_quota_limit === -1) return false;
  return b.combined_remaining_tokens <= 0;
}
